import React, { useMemo, useCallback } from "react";
import { Button, Space, Dropdown, Steps, Card, Divider } from "antd";
import { DiffOutlined, CheckCircleOutlined, RocketOutlined, MailOutlined, RollbackOutlined, DownOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { useAuth } from "../../../../hooks/useAuth";
import { getRevertMenuItems, canPerformAction } from "../utils/scheduleUtils";

/**
 * 计划状态操作组件
 * 包含状态步骤条和操作按钮
 */
const ScheduleStatusActions = ({ schedule, handlers, isEmailLoading }) => {
    const { hasPermission } = useAuth();

    // 状态步骤配置
    const statusSteps = useMemo(
        () => [
            {
                title: "草稿",
                status: "DRAFT",
                description: "计划编辑阶段",
            },
            {
                title: "已提交",
                status: "SUBMITTED",
                description: "等待航司确认",
            },
            {
                title: "航司确认",
                status: "AIRLINE_CONFIRMED",
                description: "已获得航司确认",
            },
            {
                title: "已发布",
                status: "PUBLISHED",
                description: "计划生效，转为执行航班",
            },
        ],
        [],
    );

    // 获取当前状态在步骤中的位置
    const getCurrentStep = useCallback(() => {
        const status = schedule?.status === "CONFIRMATION_PENDING" ? "SUBMITTED" : schedule?.status;
        const currentIndex = statusSteps.findIndex((step) => step.status === status);
        return currentIndex >= 0 ? currentIndex : 0;
    }, [statusSteps, schedule?.status]);

    // 获取步骤状态
    const getStepStatus = useCallback(
        (stepIndex) => {
            const currentStep = getCurrentStep();
            if (stepIndex < currentStep) return "finish";
            if (stepIndex === currentStep) {
                // 如果当前状态是已废弃，显示为错误
                if (schedule?.status === "DEPRECATED") return "error";
                return "process";
            }
            return "wait";
        },
        [getCurrentStep, schedule?.status],
    );

    // 渲染主要操作按钮（左边）
    const renderMainActions = useCallback(() => {
        if (!schedule) return [];

        const actions = [];

        switch (schedule.status) {
            case "DRAFT":
                if (hasPermission("schedule:update")) {
                    actions.push(
                        <Button key="edit_draft" type="primary" icon={<EditOutlined />} onClick={handlers.handleEditDraft}>
                            去编辑
                        </Button>,
                    );
                }
                break;

            case "SUBMITTED":
            case "CONFIRMATION_PENDING":
                if (hasPermission("schedule_official:confirm")) {
                    actions.push(
                        <Button key="airline_confirm" type="primary" icon={<CheckCircleOutlined />} onClick={schedule.status === "CONFIRMATION_PENDING" ? handlers.handleManualConfirm : handlers.handleSkipEmailConfirm}>
                            航司已确认此计划
                        </Button>,
                    );
                }
                break;

            case "AIRLINE_CONFIRMED":
                // 发布按钮 - 需要发布权限
                if (hasPermission("schedule_official:publish")) {
                    actions.push(
                        <Button key="publish" type="primary" icon={<RocketOutlined />} onClick={handlers.handlePublish}>
                            发布
                        </Button>,
                    );
                    // 发送发布邮件按钮 - 新增功能
                    actions.push(
                        <Button key="send_publish_email" icon={<MailOutlined />} onClick={handlers.handleSendPublishEmail} loading={isEmailLoading}>
                            去发送发布邮件
                        </Button>,
                    );
                    // 对比预览按钮
                    actions.push(
                        <Button key="compare_preview" icon={<DiffOutlined />} onClick={handlers.handleComparePreview}>
                            对比预览
                        </Button>,
                    );
                }
                break;

            case "PUBLISHED":
                // 已发布状态的操作
                if (hasPermission("schedule_official:read")) {
                    actions.push(
                        <Button key="compare_preview" icon={<DiffOutlined />} onClick={handlers.handleComparePreview}>
                            对比预览
                        </Button>,
                    );
                }
                break;

            default:
                break;
        }

        return actions;
    }, [schedule, hasPermission, handlers, isEmailLoading]);

    // 渲染危险操作按钮（右边）
    const renderDangerActions = useCallback(() => {
        if (!schedule) return [];

        const actions = [];

        // 添加回退操作
        if (canPerformAction(schedule.status, "revert") && hasPermission("schedule_official:revert")) {
            const revertItems = getRevertMenuItems(schedule.status, handlers);
            if (revertItems.length > 0) {
                actions.push(
                    <Dropdown key="revert_dropdown" menu={{ items: revertItems }} placement="bottomRight">
                        <Button icon={<RollbackOutlined />}>
                            回退 <DownOutlined />
                        </Button>
                    </Dropdown>,
                );
            }
        }

        // 添加废弃操作
        if (canPerformAction(schedule.status, "deprecate") && hasPermission("schedule_official:deprecate")) {
            actions.push(
                <Button key="deprecate" danger icon={<DeleteOutlined />} onClick={handlers.handleDeprecate}>
                    废弃
                </Button>,
            );
        }

        return actions;
    }, [schedule, hasPermission, handlers]);

    // 步骤条配置
    const stepsItems = useMemo(() => {
        return statusSteps.map((step, index) => ({
            title: step.title,
            description: step.description,
            status: getStepStatus(index),
            // 可以点击已完成的步骤（如果需要的话）
            disabled: getStepStatus(index) === "wait",
        }));
    }, [statusSteps, getStepStatus]);

    if (!schedule) {
        return null;
    }

    return (
        <Card className="schedule-status-section" style={{ marginBottom: 16 }}>
            {/* 状态步骤条 */}
            <div className="status-steps" style={{ margin: "24px 0" }}>
                <Steps current={getCurrentStep()} size="small" items={stepsItems} />
            </div>

            <Divider style={{ margin: "14px 0" }} />

            {/* 状态操作按钮 */}
            <div className="status-actions" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Space wrap>{renderMainActions()}</Space>
                <Space wrap>{renderDangerActions()}</Space>
            </div>
        </Card>
    );
};

export default ScheduleStatusActions;
